import { Box, Button, Flex, Heading, HStack, Input, Switch, Text, VStack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { useState } from 'react'
import { type CreateSavedPlaygroundRequest, savedPlaygroundsApi } from '@/lib/api'
import { toaster } from '~/toaster'

interface SavePlaygroundDialogProps {
  nodes: unknown[]
  edges: unknown[]
  customerName: string
  customerLogoFile: File | null
  onClose: () => void
  onSuccess: (playgroundId: string) => void
  // Pre-fill from existing playground (creates new version)
  existingPlaygroundId?: string | null
  existingName?: string
  existingIsPublic?: boolean
}

function SavePlaygroundDialog({
  nodes,
  edges,
  customerName,
  customerLogoFile,
  onClose,
  onSuccess,
  existingPlaygroundId,
  existingName = '',
  existingIsPublic = false,
}: SavePlaygroundDialogProps) {
  const queryClient = useQueryClient()
  const hasExisting = !!existingPlaygroundId
  const [name, setName] = useState(existingName)
  const [isPublic, setIsPublic] = useState(hasExisting ? existingIsPublic : true)

  const createMutation = useMutation({
    mutationFn: async (data: CreateSavedPlaygroundRequest) => {
      // First create the playground
      const playground = await savedPlaygroundsApi.create(data)

      // Then upload the logo if we have one
      if (customerLogoFile) {
        await savedPlaygroundsApi.uploadLogo(playground.id, customerLogoFile)
      }

      return playground
    },
    onSuccess: (playground) => {
      toaster.create({
        title: 'Playground saved',
        description: `Saved as "${playground.name}" (v${playground.version})`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['saved-playgrounds'] })
      onSuccess(playground.id)
      onClose()
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to save playground',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toaster.create({
        title: 'Name required',
        description: 'Please enter a name for your playground',
        type: 'error',
      })
      return
    }

    // Always create a new version (even when editing an existing playground)
    createMutation.mutate({
      name: name.trim(),
      nodes,
      edges,
      customerName: customerName || undefined,
      isPublic,
    })
  }

  const isPending = createMutation.isPending

  return (
    <Box
      position="fixed"
      inset={0}
      bg="blackAlpha.600"
      zIndex={2000}
      onClick={onClose}
      overflow="auto"
    >
      <Box
        position="relative"
        mx="auto"
        my={8}
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="450px"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex justify="space-between" align="center" p={4} borderBottomWidth="1px">
          <Heading size="md">Save Playground</Heading>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <XIcon size={16} />
          </Button>
        </Flex>

        <Box p={6}>
          <form onSubmit={handleSubmit}>
            <VStack gap={4} align="stretch">
              <Box>
                <Text fontWeight="medium" mb={1} fontSize="sm">
                  Name *
                </Text>
                <Input
                  placeholder="My Architecture Diagram"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  required
                />
                <Text fontSize="xs" color="content.secondary" mt={1}>
                  {hasExisting
                    ? 'A new version will be created with this name'
                    : 'A new version will be created if this name already exists'}
                </Text>
              </Box>

              {customerName && (
                <Box bg="bg.subtle" p={3} borderRadius="md">
                  <Text fontSize="sm" color="content.secondary">
                    Customer: <strong>{customerName}</strong>
                  </Text>
                </Box>
              )}

              <HStack justify="space-between">
                <Box>
                  <Text fontWeight="medium" fontSize="sm">
                    Make Public
                  </Text>
                  <Text fontSize="xs" color="content.secondary">
                    Others can view and load this playground
                  </Text>
                </Box>
                <Switch.Root checked={isPublic} onCheckedChange={(e) => setIsPublic(e.checked)}>
                  <Switch.HiddenInput />
                  <Switch.Control />
                </Switch.Root>
              </HStack>

              <HStack gap={2} justify="flex-end" pt={4}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" colorPalette="brand" loading={isPending}>
                  Save Playground
                </Button>
              </HStack>
            </VStack>
          </form>
        </Box>
      </Box>
    </Box>
  )
}

export default SavePlaygroundDialog
